/** Country metadata and availability come exclusively from account /auth/phone-config. */
export interface PhoneCountry {
  code: string;
  dialCode: string;
  flag: string;
  names: Record<string, string>;
  example: string;
  nationalPrefix: string;
  nationalLengths: number[];
  registrationEnabled: boolean;
}
export interface PhoneConfig { defaultCountry: string | null; countries: PhoneCountry[] }

export function decodePhoneConfig(raw: unknown): PhoneConfig {
  const c = raw as PhoneConfig | null;
  if (!c || !Array.isArray(c.countries) || !c.countries.length
      || (c.defaultCountry !== null && typeof c.defaultCountry !== 'string')) throw new Error('Invalid phone configuration');
  const codes = new Set<string>();
  const prefixes = new Set<string>();
  for (const country of c.countries) {
    if (!country || typeof country.code !== 'string' || !/^[A-Z]{2}$/.test(country.code)
        || typeof country.dialCode !== 'string' || !/^\+[1-9]\d{0,3}$/.test(country.dialCode)
        || codes.has(country.code) || prefixes.has(country.dialCode)
        || typeof country.flag !== 'string' || typeof country.example !== 'string'
        || !country.names || typeof country.names.en !== 'string'
        || Object.values(country.names).some((v) => typeof v !== 'string')
        || typeof country.registrationEnabled !== 'boolean'
        || typeof country.nationalPrefix !== 'string' || !/^\d*$/.test(country.nationalPrefix)
        || !Array.isArray(country.nationalLengths) || !country.nationalLengths.length
        || country.nationalLengths.some((n) => !Number.isInteger(n) || n < 1 || n > 14)) throw new Error('Invalid phone country');
    codes.add(country.code); prefixes.add(country.dialCode);
  }
  const enabled = c.countries.filter((country) => country.registrationEnabled);
  if (enabled.length ? !enabled.some((country) => country.code === c.defaultCountry) : c.defaultCountry !== null) {
    throw new Error('Invalid default phone country');
  }
  return c;
}

export function availableCountries(config: PhoneConfig | null, mode: 'login' | 'register') {
  return config?.countries.filter((c) => mode === 'login' || c.registrationEnabled) ?? [];
}

function compact(raw: string): string {
  return raw.replace(/[\s\-().]+/g, '').replace(/^00/, '+');
}

export function parsePhoneInput(raw: string, country: string, countries: PhoneCountry[]) {
  const value = compact(raw);
  const match = countries.find((c) => value.startsWith(c.dialCode));
  return match ? { country: match.code, number: value.slice(match.dialCode.length) } : { country, number: raw };
}

export function phoneIdentifier(country: string, raw: string, countries: PhoneCountry[]): string | null {
  const parsed = parsePhoneInput(raw, country, countries);
  const selected = countries.find((c) => c.code === parsed.country);
  if (!selected) return null;
  let national = compact(parsed.number);
  if (selected.nationalPrefix && national.startsWith(selected.nationalPrefix)
      && selected.nationalLengths.includes(national.length - selected.nationalPrefix.length)) {
    national = national.slice(selected.nationalPrefix.length);
  }
  if (!/^\d+$/.test(national)) return null;
  const identifier = selected.dialCode + national;
  return /^\+[1-9]\d{7,14}$/.test(identifier) ? identifier : null;
}
